import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import {
  Calculator,
  Scale,
  Timer,
  Play,
  Pause,
  RotateCcw,
  Volume2,
  Vibrate,
  Settings,
  ChevronDown,
  Info,
  Dumbbell,
  Target,
  TrendingUp,
  Check,
  Bell,
  Music,
  PlayCircle,
  Zap,
  Activity,
  Bike,
  Wind,
  Flame,
  Sparkles,
  Loader2,
  SkipForward,
  SkipBack,
  Link2,
  Link2Off
} from 'lucide-react';

const FORMULAS = {
  epley: { name: 'Epley', description: 'Most commonly used, good for 5-10 reps' },
  brzycki: { name: 'Brzycki', description: 'Very accurate for lower rep ranges' },
  lombardi: { name: 'Lombardi', description: 'Good all-around formula' },
  oconner: { name: "O'Conner", description: 'Conservative estimate for beginners' },
  lander: { name: 'Lander', description: 'Research-based formula' },
  bachle: { name: 'Bachle', description: 'Simple linear estimate' }
};

const PLATES_LBS = [45, 35, 25, 10, 5, 2.5];
const PLATES_KG = [25, 20, 15, 10, 5, 2.5, 1.25];
const BAR_WEIGHT_LBS = 45;
const BAR_WEIGHT_KG = 20;

export default function FitnessToolsPage() {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('1rm');
  const [unit, setUnit] = useState('lbs');
  
  // 1RM Calculator State
  const [weight, setWeight] = useState('');
  const [reps, setReps] = useState('');
  const [formula, setFormula] = useState('epley');
  const [oneRmResult, setOneRmResult] = useState(null);
  const [showAllFormulas, setShowAllFormulas] = useState(false);
  
  // Plate Calculator State
  const [targetWeight, setTargetWeight] = useState('');
  const [plateResult, setPlateResult] = useState(null);
  
  // Timer State
  const [timerMode, setTimerMode] = useState('stopwatch'); // 'stopwatch', 'countdown', or 'interval'
  const [time, setTime] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [countdownMinutes, setCountdownMinutes] = useState(1);
  const [countdownSeconds, setCountdownSeconds] = useState(30);
  const [alertType, setAlertType] = useState('music'); // 'sound', 'vibrate', or 'music'
  const [selectedSong, setSelectedSong] = useState('level_up'); // Default to Level Up vibe
  const intervalRef = useRef(null);
  const audioRef = useRef(null);
  const audioContextRef = useRef(null);
  
  // Interval Training State
  const [workSeconds, setWorkSeconds] = useState(30);
  const [restSeconds, setRestSeconds] = useState(10);
  const [totalRounds, setTotalRounds] = useState(8);
  const [currentRound, setCurrentRound] = useState(1);
  const [intervalPhase, setIntervalPhase] = useState('ready'); // 'ready', 'work', 'rest', 'complete'
  const [phaseTimeRemaining, setPhaseTimeRemaining] = useState(0);
  const [includeWarmup, setIncludeWarmup] = useState(false);
  const [includeCooldown, setIncludeCooldown] = useState(false);
  const [warmupSeconds, setWarmupSeconds] = useState(60);
  const [cooldownSeconds, setCooldownSeconds] = useState(60);
  const phaseAudioRef = useRef(null);
  const workPlaylistRef = useRef(null);
  const restPlaylistRef = useRef(null);
  const workTrackIndexRef = useRef(0);
  const restTrackIndexRef = useRef(0);
  
  // Interval presets
  const INTERVAL_PRESETS = [
    { name: 'Tabata', work: 20, rest: 10, rounds: 8, description: 'Classic high-intensity' },
    { name: 'EMOM', work: 40, rest: 20, rounds: 10, description: 'Every Minute on the Minute' },
    { name: 'Boxing', work: 180, rest: 60, rounds: 12, description: '3 min rounds' },
    { name: '30/30', work: 30, rest: 30, rounds: 10, description: 'Balanced intervals' },
    { name: 'Sprint', work: 15, rest: 45, rounds: 8, description: 'Short bursts' },
    { name: 'Endurance', work: 60, rest: 15, rounds: 6, description: 'Longer work periods' },
  ];
  
  // Workout Mode State (Spotify Integration)
  const [workoutModeEnabled, setWorkoutModeEnabled] = useState(false);
  const [spotifyConnected, setSpotifyConnected] = useState(false);
  const [spotifyToken, setSpotifyToken] = useState(null);
  const [workoutType, setWorkoutType] = useState('hiit');
  const [workoutPlaylist, setWorkoutPlaylist] = useState(null);
  const [currentTrack, setCurrentTrack] = useState(null);
  const [isPlayingMusic, setIsPlayingMusic] = useState(false);
  const [isGeneratingPlaylist, setIsGeneratingPlaylist] = useState(false);
  const previewAudioRef = useRef(null);
  const currentTrackIndexRef = useRef(0);
  
  // Workout types for Workout Mode
  const WORKOUT_TYPES = [
    { id: 'hiit', name: 'HIIT', icon: Zap, color: 'text-red-500' },
    { id: 'strength', name: 'Strength', icon: Dumbbell, color: 'text-orange-500' },
    { id: 'cardio', name: 'Cardio', icon: Activity, color: 'text-pink-500' },
    { id: 'cycling', name: 'Cycling', icon: Bike, color: 'text-blue-500' },
    { id: 'yoga', name: 'Yoga', icon: Wind, color: 'text-green-500' },
    { id: 'crossfit', name: 'CrossFit', icon: Flame, color: 'text-yellow-500' },
  ];
  
  // Check for Spotify token on mount
  useEffect(() => {
    const token = localStorage.getItem('spotify_access_token');
    if (token) {
      setSpotifyToken(token);
      setSpotifyConnected(true);
    }
  }, []);
  
  // High-energy song options (inspired by the requested songs)
  const PUMP_UP_SONGS = {
    level_up: { 
      name: 'Level Up', 
      artist: 'Ciara-inspired',
      description: 'High-energy beat drops',
      icon: '🔥',
      bpm: 128
    },
    remember_the_name: { 
      name: 'Remember The Name', 
      artist: 'Fort Minor-inspired',
      description: 'Epic motivational crescendo',
      icon: '💪',
      bpm: 88
    },
    dont_stop_music: { 
      name: "Don't Stop", 
      artist: 'Rihanna-inspired',
      description: 'Pulsing club energy',
      icon: '🎵',
      bpm: 123
    }
  };

  // Calculate 1RM
  const calculate1RM = useCallback((w, r, f) => {
    if (!w || !r || r < 1) return 0;
    const weight = parseFloat(w);
    const reps = parseInt(r);
    
    switch (f) {
      case 'epley':
        return weight * (1 + reps / 30);
      case 'brzycki':
        return reps >= 37 ? weight * 2 : weight * 36 / (37 - reps);
      case 'lombardi':
        return weight * Math.pow(reps, 0.10);
      case 'oconner':
        return weight * (1 + reps / 40);
      case 'lander':
        const denom = 101.3 - 2.67123 * reps;
        return denom <= 0 ? weight * 2 : 100 * weight / denom;
      case 'bachle':
        return weight * (1 + 0.033 * reps);
      default:
        return weight * (1 + reps / 30);
    }
  }, []);

  // Calculate weight for target reps
  const calculateWeightForReps = useCallback((oneRm, targetReps, f) => {
    if (targetReps <= 1) return oneRm;
    
    switch (f) {
      case 'epley':
        return oneRm / (1 + targetReps / 30);
      case 'brzycki':
        return oneRm * (37 - targetReps) / 36;
      case 'lombardi':
        return oneRm / Math.pow(targetReps, 0.10);
      case 'oconner':
        return oneRm / (1 + targetReps / 40);
      case 'lander':
        return oneRm * (101.3 - 2.67123 * targetReps) / 100;
      case 'bachle':
        return oneRm / (1 + 0.033 * targetReps);
      default:
        return oneRm / (1 + targetReps / 30);
    }
  }, []);

  // Handle 1RM calculation
  const handleCalculate1RM = () => {
    if (!weight || !reps) {
      toast.error('Please enter weight and reps');
      return;
    }
    
    const w = parseFloat(weight);
    const r = parseInt(reps);
    
    if (w <= 0 || r < 1 || r > 30) {
      toast.error('Invalid input. Reps must be 1-30');
      return;
    }
    
    const estimated1RM = calculate1RM(w, r, formula);
    
    // Generate rep chart
    const repChart = [];
    for (let targetReps = 1; targetReps <= 12; targetReps++) {
      const weightForReps = calculateWeightForReps(estimated1RM, targetReps, formula);
      repChart.push({
        reps: targetReps,
        weight: Math.round(weightForReps * 10) / 10,
        percentage: Math.round((weightForReps / estimated1RM) * 1000) / 10
      });
    }
    
    // Calculate all formulas
    const allFormulas = {};
    Object.keys(FORMULAS).forEach(f => {
      allFormulas[f] = {
        name: FORMULAS[f].name,
        estimated1RM: Math.round(calculate1RM(w, r, f) * 10) / 10
      };
    });
    
    const avg = Object.values(allFormulas).reduce((sum, f) => sum + f.estimated1RM, 0) / Object.keys(allFormulas).length;
    
    setOneRmResult({
      estimated1RM: Math.round(estimated1RM * 10) / 10,
      repChart,
      allFormulas,
      average: Math.round(avg * 10) / 10
    });
  };

  // Handle plate calculation
  const handleCalculatePlates = () => {
    if (!targetWeight) {
      toast.error('Please enter target weight');
      return;
    }
    
    const target = parseFloat(targetWeight);
    const barWeight = unit === 'lbs' ? BAR_WEIGHT_LBS : BAR_WEIGHT_KG;
    const plates = unit === 'lbs' ? PLATES_LBS : PLATES_KG;
    
    if (target <= barWeight) {
      setPlateResult({
        valid: false,
        error: `Target weight must be greater than bar weight (${barWeight} ${unit})`
      });
      return;
    }
    
    const weightPerSide = (target - barWeight) / 2;
    let remaining = weightPerSide;
    const platesNeeded = [];
    
    for (const plate of plates) {
      while (remaining >= plate) {
        platesNeeded.push(plate);
        remaining -= plate;
      }
    }
    
    // Group plates
    const plateCounts = {};
    platesNeeded.forEach(p => {
      plateCounts[p] = (plateCounts[p] || 0) + 1;
    });
    
    const breakdown = Object.entries(plateCounts)
      .sort((a, b) => parseFloat(b[0]) - parseFloat(a[0]))
      .map(([weight, count]) => ({ weight: parseFloat(weight), count }));
    
    const totalPlateWeight = platesNeeded.reduce((sum, p) => sum + p, 0) * 2;
    
    setPlateResult({
      valid: true,
      breakdown,
      barWeight,
      totalPlateWeight,
      actualTotal: barWeight + totalPlateWeight,
      remainder: Math.round(remaining * 2 * 100) / 100
    });
  };

  // Timer functions
  useEffect(() => {
    if (isRunning) {
      intervalRef.current = setInterval(() => {
        if (timerMode === 'interval') {
          // Interval training mode
          setPhaseTimeRemaining(prev => {
            if (prev <= 10) {
              // Phase complete - handle transition
              handlePhaseTransition();
              return 0;
            }
            return prev - 10;
          });
          setTime(prev => prev + 10); // Track total elapsed time
        } else {
          setTime(prev => {
            if (timerMode === 'countdown') {
              if (prev <= 0) {
                setIsRunning(false);
                triggerAlert();
                return 0;
              }
              return prev - 10;
            }
            return prev + 10;
          });
        }
      }, 10);
    } else {
      clearInterval(intervalRef.current);
    }
    
    return () => clearInterval(intervalRef.current);
  }, [isRunning, timerMode]);
  
  // Handle interval phase transitions
  const handlePhaseTransition = useCallback(() => {
    if (intervalPhase === 'ready') return;
    
    if (intervalPhase === 'warmup') {
      // Warmup complete, start first work phase
      setIntervalPhase('work');
      setPhaseTimeRemaining(workSeconds * 1000);
      setCurrentRound(1);
      playPhaseChangeSound('work');
      if (workoutModeEnabled && spotifyConnected) {
        switchToWorkMusic();
      }
    } else if (intervalPhase === 'work') {
      // Work complete
      if (currentRound >= totalRounds) {
        // All rounds complete
        if (includeCooldown) {
          setIntervalPhase('cooldown');
          setPhaseTimeRemaining(cooldownSeconds * 1000);
          playPhaseChangeSound('cooldown');
          if (workoutModeEnabled && spotifyConnected) {
            switchToRestMusic();
          }
        } else {
          // Workout complete!
          setIntervalPhase('complete');
          setIsRunning(false);
          triggerAlert();
          if (workoutModeEnabled) {
            stopIntervalMusic();
          }
        }
      } else {
        // Start rest phase
        setIntervalPhase('rest');
        setPhaseTimeRemaining(restSeconds * 1000);
        playPhaseChangeSound('rest');
        if (workoutModeEnabled && spotifyConnected) {
          switchToRestMusic();
        }
      }
    } else if (intervalPhase === 'rest') {
      // Rest complete, start next work phase
      setIntervalPhase('work');
      setPhaseTimeRemaining(workSeconds * 1000);
      setCurrentRound(prev => prev + 1);
      playPhaseChangeSound('work');
      if (workoutModeEnabled && spotifyConnected) {
        switchToWorkMusic();
      }
    } else if (intervalPhase === 'cooldown') {
      // Cooldown complete, workout done!
      setIntervalPhase('complete');
      setIsRunning(false);
      triggerAlert();
      if (workoutModeEnabled) {
        stopIntervalMusic();
      }
    }
  }, [intervalPhase, currentRound, totalRounds, workSeconds, restSeconds, includeCooldown, cooldownSeconds, workoutModeEnabled, spotifyConnected]);
  
  // Play phase change sound
  const playPhaseChangeSound = (phase) => {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const masterGain = audioContext.createGain();
    masterGain.connect(audioContext.destination);
    masterGain.gain.value = 0.5;
    
    if (phase === 'work') {
      // Energetic ascending tones for WORK
      [523, 659, 784].forEach((freq, i) => {
        setTimeout(() => {
          const osc = audioContext.createOscillator();
          const gain = audioContext.createGain();
          osc.connect(gain);
          gain.connect(masterGain);
          osc.type = 'square';
          osc.frequency.value = freq;
          gain.gain.setValueAtTime(0.4, audioContext.currentTime);
          gain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.15);
          osc.start();
          osc.stop(audioContext.currentTime + 0.2);
        }, i * 100);
      });
    } else if (phase === 'rest') {
      // Calming descending tones for REST
      [392, 330, 262].forEach((freq, i) => {
        setTimeout(() => {
          const osc = audioContext.createOscillator();
          const gain = audioContext.createGain();
          osc.connect(gain);
          gain.connect(masterGain);
          osc.type = 'sine';
          osc.frequency.value = freq;
          gain.gain.setValueAtTime(0.3, audioContext.currentTime);
          gain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.25);
          osc.start();
          osc.stop(audioContext.currentTime + 0.3);
        }, i * 150);
      });
    } else if (phase === 'cooldown') {
      // Gentle chime for cooldown
      const osc = audioContext.createOscillator();
      const gain = audioContext.createGain();
      osc.connect(gain);
      gain.connect(masterGain);
      osc.type = 'sine';
      osc.frequency.value = 440;
      gain.gain.setValueAtTime(0.3, audioContext.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      osc.start();
      osc.stop(audioContext.currentTime + 0.6);
    }
  };
  
  // Switch to high-energy work music
  const switchToWorkMusic = async () => {
    // Stop current music
    if (phaseAudioRef.current) {
      phaseAudioRef.current.pause();
      phaseAudioRef.current = null;
    }
    
    // Use work playlist or generate one
    if (!workPlaylistRef.current) {
      try {
        const response = await axios.post(
          `${API}/spotify/workout-dj/generate?access_token=${spotifyToken}`,
          {
            workout_type: 'hiit', // High energy for work phases
            duration_minutes: Math.ceil((workSeconds * totalRounds) / 60) + 5,
            include_warmup: false,
            include_cooldown: false
          }
        );
        workPlaylistRef.current = response.data.tracks;
      } catch (error) {
        console.error('Failed to generate work playlist:', error);
        return;
      }
    }
    
    // Play from work playlist
    if (workPlaylistRef.current && workPlaylistRef.current.length > 0) {
      const track = workPlaylistRef.current[workTrackIndexRef.current % workPlaylistRef.current.length];
      if (track.preview_url) {
        const audio = new Audio(track.preview_url);
        audio.volume = 0.6;
        phaseAudioRef.current = audio;
        audio.play();
        setCurrentTrack(track);
        setIsPlayingMusic(true);
        
        audio.onended = () => {
          workTrackIndexRef.current++;
          if (intervalPhase === 'work' && isRunning) {
            switchToWorkMusic();
          }
        };
      } else {
        // Skip to next track
        workTrackIndexRef.current++;
        switchToWorkMusic();
      }
    }
  };
  
  // Switch to lower-energy rest music
  const switchToRestMusic = async () => {
    // Stop current music
    if (phaseAudioRef.current) {
      phaseAudioRef.current.pause();
      phaseAudioRef.current = null;
    }
    
    // Use rest playlist or generate one
    if (!restPlaylistRef.current) {
      try {
        const response = await axios.post(
          `${API}/spotify/workout-dj/generate?access_token=${spotifyToken}`,
          {
            workout_type: 'cooldown', // Lower energy for rest phases
            duration_minutes: Math.ceil((restSeconds * totalRounds) / 60) + 5,
            include_warmup: false,
            include_cooldown: false
          }
        );
        restPlaylistRef.current = response.data.tracks;
      } catch (error) {
        console.error('Failed to generate rest playlist:', error);
        return;
      }
    }
    
    // Play from rest playlist
    if (restPlaylistRef.current && restPlaylistRef.current.length > 0) {
      const track = restPlaylistRef.current[restTrackIndexRef.current % restPlaylistRef.current.length];
      if (track.preview_url) {
        const audio = new Audio(track.preview_url);
        audio.volume = 0.4; // Lower volume for rest
        phaseAudioRef.current = audio;
        audio.play();
        setCurrentTrack(track);
        setIsPlayingMusic(true);
        
        audio.onended = () => {
          restTrackIndexRef.current++;
          if ((intervalPhase === 'rest' || intervalPhase === 'cooldown') && isRunning) {
            switchToRestMusic();
          }
        };
      } else {
        // Skip to next track
        restTrackIndexRef.current++;
        switchToRestMusic();
      }
    }
  };
  
  // Stop interval music
  const stopIntervalMusic = () => {
    if (phaseAudioRef.current) {
      phaseAudioRef.current.pause();
      phaseAudioRef.current = null;
    }
    setIsPlayingMusic(false);
    setCurrentTrack(null);
  };
  
  // Apply interval preset
  const applyIntervalPreset = (preset) => {
    setWorkSeconds(preset.work);
    setRestSeconds(preset.rest);
    setTotalRounds(preset.rounds);
    toast.success(`Applied ${preset.name} preset`);
  };
  
  // Calculate total interval workout time
  const calculateTotalIntervalTime = () => {
    let total = 0;
    if (includeWarmup) total += warmupSeconds;
    total += (workSeconds + restSeconds) * totalRounds - restSeconds; // No rest after last round
    if (includeCooldown) total += cooldownSeconds;
    return total;
  };

  // Play high-energy music alert inspired by the selected song
  const playHighEnergyAlert = useCallback((songKey) => {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    audioContextRef.current = audioContext;
    const masterGain = audioContext.createGain();
    masterGain.connect(audioContext.destination);
    masterGain.gain.value = 0.6;

    const songConfig = PUMP_UP_SONGS[songKey];
    const bpm = songConfig?.bpm || 128;
    const beatInterval = 60 / bpm;
    
    // Different patterns for each song style
    if (songKey === 'level_up') {
      // Level Up - High energy EDM drops with rising synths
      const frequencies = [262, 330, 392, 523, 659, 784, 1047]; // C major scale going up
      frequencies.forEach((freq, i) => {
        setTimeout(() => {
          const osc = audioContext.createOscillator();
          const gain = audioContext.createGain();
          osc.connect(gain);
          gain.connect(masterGain);
          
          osc.type = 'sawtooth';
          osc.frequency.setValueAtTime(freq, audioContext.currentTime);
          gain.gain.setValueAtTime(0.5, audioContext.currentTime);
          gain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
          
          osc.start();
          osc.stop(audioContext.currentTime + 0.35);
        }, i * 120);
      });
      
      // Add bass drops
      [0, 400, 800, 1200].forEach((delay) => {
        setTimeout(() => {
          const bassOsc = audioContext.createOscillator();
          const bassGain = audioContext.createGain();
          bassOsc.connect(bassGain);
          bassGain.connect(masterGain);
          bassOsc.type = 'sine';
          bassOsc.frequency.value = 80;
          bassGain.gain.setValueAtTime(0.7, audioContext.currentTime);
          bassGain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);
          bassOsc.start();
          bassOsc.stop(audioContext.currentTime + 0.25);
        }, delay);
      });
      
    } else if (songKey === 'remember_the_name') {
      // Remember The Name - Epic orchestral buildup
      const epicChords = [
        [196, 247, 294], // G chord
        [220, 277, 330], // A chord  
        [262, 330, 392], // C chord
        [294, 370, 440], // D chord
        [330, 415, 494], // E chord - climax
      ];
      
      epicChords.forEach((chord, i) => {
        setTimeout(() => {
          chord.forEach(freq => {
            const osc = audioContext.createOscillator();
            const gain = audioContext.createGain();
            osc.connect(gain);
            gain.connect(masterGain);
            osc.type = 'square';
            osc.frequency.value = freq;
            const volume = i === 4 ? 0.4 : 0.25;
            gain.gain.setValueAtTime(volume, audioContext.currentTime);
            gain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
            osc.start();
            osc.stop(audioContext.currentTime + 0.55);
          });
        }, i * 300);
      });
      
    } else if (songKey === 'dont_stop_music') {
      // Don't Stop The Music - Pulsing club beat
      const pulsePattern = [0, 150, 300, 450, 600, 750, 900, 1050];
      
      pulsePattern.forEach((delay, i) => {
        setTimeout(() => {
          // Kick drum
          const kickOsc = audioContext.createOscillator();
          const kickGain = audioContext.createGain();
          kickOsc.connect(kickGain);
          kickGain.connect(masterGain);
          kickOsc.type = 'sine';
          kickOsc.frequency.setValueAtTime(150, audioContext.currentTime);
          kickOsc.frequency.exponentialRampToValueAtTime(50, audioContext.currentTime + 0.1);
          kickGain.gain.setValueAtTime(0.6, audioContext.currentTime);
          kickGain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.15);
          kickOsc.start();
          kickOsc.stop(audioContext.currentTime + 0.2);
          
          // Hi-hat on off-beats
          if (i % 2 === 1) {
            const noise = audioContext.createOscillator();
            const noiseGain = audioContext.createGain();
            noise.connect(noiseGain);
            noiseGain.connect(masterGain);
            noise.type = 'triangle';
            noise.frequency.value = 8000;
            noiseGain.gain.setValueAtTime(0.2, audioContext.currentTime);
            noiseGain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.05);
            noise.start();
            noise.stop(audioContext.currentTime + 0.1);
          }
        }, delay);
      });
      
      // Synth melody
      const melody = [523, 587, 659, 698, 784];
      melody.forEach((freq, i) => {
        setTimeout(() => {
          const synthOsc = audioContext.createOscillator();
          const synthGain = audioContext.createGain();
          synthOsc.connect(synthGain);
          synthGain.connect(masterGain);
          synthOsc.type = 'sawtooth';
          synthOsc.frequency.value = freq;
          synthGain.gain.setValueAtTime(0.3, audioContext.currentTime);
          synthGain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);
          synthOsc.start();
          synthOsc.stop(audioContext.currentTime + 0.25);
        }, 200 + i * 200);
      });
    }
    
    // Cleanup after 2 seconds
    setTimeout(() => {
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
      }
    }, 2000);
  }, []);

  const triggerAlert = () => {
    if (alertType === 'music') {
      // Play high-energy music alert
      playHighEnergyAlert(selectedSong);
      toast.success(`TIME'S UP! 🔥 ${PUMP_UP_SONGS[selectedSong].name}`, {
        duration: 3000
      });
    } else if (alertType === 'sound') {
      // Play simple beep sound
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      gainNode.gain.value = 0.5;
      
      oscillator.start();
      setTimeout(() => {
        oscillator.stop();
        // Second beep
        const osc2 = audioContext.createOscillator();
        osc2.connect(gainNode);
        osc2.frequency.value = 1000;
        osc2.start();
        setTimeout(() => osc2.stop(), 200);
      }, 200);
      toast.success('Time is up!');
    } else {
      // Vibrate
      if ('vibrate' in navigator) {
        navigator.vibrate([200, 100, 200, 100, 200]);
      }
      toast.success('Time is up!');
    }
  };
  
  // Test the selected song
  const testAlert = () => {
    triggerAlert();
  };

  // Workout Mode Functions
  const generateWorkoutPlaylist = async () => {
    if (!spotifyToken) {
      toast.error('Please connect to Spotify first');
      return null;
    }
    
    setIsGeneratingPlaylist(true);
    try {
      // Calculate duration from countdown or use default 30 min for stopwatch
      const durationMinutes = timerMode === 'countdown' 
        ? Math.ceil((countdownMinutes * 60 + countdownSeconds) / 60)
        : 30;
      
      const response = await axios.post(
        `${API}/spotify/workout-dj/generate?access_token=${spotifyToken}`,
        {
          workout_type: workoutType,
          duration_minutes: Math.max(durationMinutes, 15), // Minimum 15 min
          include_warmup: false,
          include_cooldown: false
        }
      );
      
      setWorkoutPlaylist(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to generate workout playlist:', error);
      if (error.response?.status === 401) {
        setSpotifyConnected(false);
        localStorage.removeItem('spotify_access_token');
        toast.error('Spotify session expired. Please reconnect.');
      } else {
        toast.error('Failed to generate playlist');
      }
      return null;
    } finally {
      setIsGeneratingPlaylist(false);
    }
  };
  
  const playTrack = (track, trackList, trackIndex) => {
    // Stop any previous audio
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
      previewAudioRef.current = null;
    }
    
    if (!track.preview_url) {
      // Skip to next track if no preview available
      if (trackList && trackIndex < trackList.length - 1) {
        playTrack(trackList[trackIndex + 1], trackList, trackIndex + 1);
      }
      return;
    }
    
    const audio = new Audio(track.preview_url);
    audio.volume = 0.5;
    previewAudioRef.current = audio;
    currentTrackIndexRef.current = trackIndex;
    
    audio.play();
    setCurrentTrack(track);
    setIsPlayingMusic(true);
    
    audio.onended = () => {
      // Auto-play next track
      if (trackList && trackIndex < trackList.length - 1) {
        playTrack(trackList[trackIndex + 1], trackList, trackIndex + 1);
      } else {
        setIsPlayingMusic(false);
        setCurrentTrack(null);
      }
    };
  };
  
  const toggleMusicPlayback = () => {
    if (previewAudioRef.current) {
      if (isPlayingMusic) {
        previewAudioRef.current.pause();
      } else {
        previewAudioRef.current.play();
      }
      setIsPlayingMusic(!isPlayingMusic);
    }
  };
  
  const skipToNextTrack = () => {
    if (workoutPlaylist && currentTrackIndexRef.current < workoutPlaylist.tracks.length - 1) {
      playTrack(
        workoutPlaylist.tracks[currentTrackIndexRef.current + 1],
        workoutPlaylist.tracks,
        currentTrackIndexRef.current + 1
      );
    }
  };
  
  const skipToPreviousTrack = () => {
    if (workoutPlaylist && currentTrackIndexRef.current > 0) {
      playTrack(
        workoutPlaylist.tracks[currentTrackIndexRef.current - 1],
        workoutPlaylist.tracks,
        currentTrackIndexRef.current - 1
      );
    }
  };
  
  const stopMusic = () => {
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
      previewAudioRef.current = null;
    }
    setIsPlayingMusic(false);
    setCurrentTrack(null);
  };
  
  const connectToSpotify = async () => {
    try {
      const response = await axios.get(`${API}/spotify/auth/login`);
      window.location.href = response.data.auth_url;
    } catch (error) {
      toast.error('Failed to connect to Spotify');
    }
  };

  const startTimer = async () => {
    if (timerMode === 'countdown' && time === 0) {
      setTime((countdownMinutes * 60 + countdownSeconds) * 1000);
    }
    
    // Handle interval mode
    if (timerMode === 'interval') {
      if (intervalPhase === 'ready' || intervalPhase === 'complete') {
        // Starting fresh
        setTime(0);
        setCurrentRound(1);
        workPlaylistRef.current = null;
        restPlaylistRef.current = null;
        workTrackIndexRef.current = 0;
        restTrackIndexRef.current = 0;
        
        if (includeWarmup) {
          setIntervalPhase('warmup');
          setPhaseTimeRemaining(warmupSeconds * 1000);
          toast.success('🔥 Warmup started!');
          if (workoutModeEnabled && spotifyConnected) {
            switchToRestMusic(); // Lower energy for warmup
          }
        } else {
          setIntervalPhase('work');
          setPhaseTimeRemaining(workSeconds * 1000);
          playPhaseChangeSound('work');
          toast.success('💪 WORK! Round 1');
          if (workoutModeEnabled && spotifyConnected) {
            switchToWorkMusic();
          }
        }
      }
      setIsRunning(true);
      return;
    }
    
    // If Workout Mode is enabled for non-interval modes, generate and play playlist
    if (workoutModeEnabled && spotifyConnected && timerMode !== 'interval') {
      const playlist = await generateWorkoutPlaylist();
      if (playlist && playlist.tracks.length > 0) {
        toast.success(`🎵 Starting ${WORKOUT_TYPES.find(t => t.id === workoutType)?.name} workout playlist!`);
        playTrack(playlist.tracks[0], playlist.tracks, 0);
      }
    }
    
    setIsRunning(true);
  };

  const pauseTimer = () => {
    setIsRunning(false);
    // Also pause music if Workout Mode is active
    if (workoutModeEnabled && isPlayingMusic) {
      if (previewAudioRef.current) {
        previewAudioRef.current.pause();
      }
      if (phaseAudioRef.current) {
        phaseAudioRef.current.pause();
      }
      setIsPlayingMusic(false);
    }
  };

  const resetTimer = () => {
    setIsRunning(false);
    // Stop music if Workout Mode is active
    if (workoutModeEnabled) {
      stopMusic();
      stopIntervalMusic();
      workPlaylistRef.current = null;
      restPlaylistRef.current = null;
      setWorkoutPlaylist(null);
    }
    
    // Reset interval state
    if (timerMode === 'interval') {
      setIntervalPhase('ready');
      setCurrentRound(1);
      setPhaseTimeRemaining(0);
      setTime(0);
    } else if (timerMode === 'countdown') {
      setTime((countdownMinutes * 60 + countdownSeconds) * 1000);
    } else {
      setTime(0);
    }
  };

  const formatTime = (ms) => {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    const milliseconds = Math.floor((ms % 1000) / 10);
    
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}.${milliseconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className="space-y-6" data-testid="fitness-tools-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Fitness Tools</h1>
          <p className="text-gray-400">1RM Calculator, Plate Calculator & Timer</p>
        </div>
        
        {/* Unit Toggle */}
        <div className="flex items-center gap-2 bg-card border border-border rounded-lg p-1">
          <button
            onClick={() => setUnit('lbs')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              unit === 'lbs' ? 'bg-primary text-white' : 'text-gray-400 hover:text-white'
            }`}
          >
            LBS
          </button>
          <button
            onClick={() => setUnit('kg')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              unit === 'kg' ? 'bg-primary text-white' : 'text-gray-400 hover:text-white'
            }`}
          >
            KG
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-border pb-2">
        <button
          onClick={() => setActiveTab('1rm')}
          className={`flex items-center gap-2 px-4 py-2 rounded-t-lg transition-colors ${
            activeTab === '1rm' ? 'bg-primary text-white' : 'text-gray-400 hover:text-white'
          }`}
          data-testid="tab-1rm"
        >
          <Calculator className="w-4 h-4" />
          1RM Calculator
        </button>
        <button
          onClick={() => setActiveTab('plates')}
          className={`flex items-center gap-2 px-4 py-2 rounded-t-lg transition-colors ${
            activeTab === 'plates' ? 'bg-primary text-white' : 'text-gray-400 hover:text-white'
          }`}
          data-testid="tab-plates"
        >
          <Scale className="w-4 h-4" />
          Plate Calculator
        </button>
        <button
          onClick={() => setActiveTab('timer')}
          className={`flex items-center gap-2 px-4 py-2 rounded-t-lg transition-colors ${
            activeTab === 'timer' ? 'bg-primary text-white' : 'text-gray-400 hover:text-white'
          }`}
          data-testid="tab-timer"
        >
          <Timer className="w-4 h-4" />
          Timer
        </button>
      </div>

      {/* 1RM Calculator Tab */}
      {activeTab === '1rm' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Input Card */}
          <div className="bg-card border border-border rounded-xl p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Target className="w-5 h-5 text-primary" />
              Calculate Your 1 Rep Max
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Weight Lifted ({unit})</label>
                <input
                  type="number"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  className="w-full px-4 py-3 bg-secondary border border-border rounded-lg text-white text-lg"
                  placeholder={`e.g., ${unit === 'lbs' ? '260' : '120'}`}
                  data-testid="1rm-weight-input"
                />
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-1">Reps Completed</label>
                <input
                  type="number"
                  value={reps}
                  onChange={(e) => setReps(e.target.value)}
                  min="1"
                  max="30"
                  className="w-full px-4 py-3 bg-secondary border border-border rounded-lg text-white text-lg"
                  placeholder="e.g., 5"
                  data-testid="1rm-reps-input"
                />
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-1">Formula</label>
                <select
                  value={formula}
                  onChange={(e) => setFormula(e.target.value)}
                  className="w-full px-4 py-3 bg-secondary border border-border rounded-lg text-white"
                  data-testid="1rm-formula-select"
                >
                  {Object.entries(FORMULAS).map(([key, { name, description }]) => (
                    <option key={key} value={key}>{name} - {description}</option>
                  ))}
                </select>
              </div>
              
              <Button 
                onClick={handleCalculate1RM}
                className="w-full bg-primary hover:bg-red-700 text-lg py-6"
                data-testid="calculate-1rm-btn"
              >
                <Calculator className="w-5 h-5 mr-2" />
                Calculate 1RM
              </Button>
            </div>
          </div>

          {/* Results Card */}
          <div className="bg-card border border-border rounded-xl p-6">
            {oneRmResult ? (
              <div className="space-y-6">
                {/* Main Result */}
                <div className="text-center p-6 bg-gradient-to-br from-primary/20 to-red-900/20 rounded-xl border border-primary/30">
                  <div className="text-gray-400 text-sm mb-1">Estimated 1 Rep Max</div>
                  <div className="text-5xl font-bold text-white mb-2">
                    {oneRmResult.estimated1RM} <span className="text-2xl text-gray-400">{unit}</span>
                  </div>
                  <div className="text-sm text-gray-400">Using {FORMULAS[formula].name} Formula</div>
                </div>
                
                {/* Rep Chart */}
                <div>
                  <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-primary" />
                    Rep Chart
                  </h3>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {oneRmResult.repChart.map(({ reps, weight, percentage }) => (
                      <div 
                        key={reps}
                        className={`p-3 rounded-lg text-center ${
                          reps === 1 ? 'bg-primary/20 border border-primary/50' : 'bg-secondary/50'
                        }`}
                      >
                        <div className="text-xs text-gray-400">{reps} rep{reps > 1 ? 's' : ''}</div>
                        <div className="text-white font-semibold">{weight}</div>
                        <div className="text-xs text-gray-500">{percentage}%</div>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* All Formulas Comparison */}
                <div>
                  <button
                    onClick={() => setShowAllFormulas(!showAllFormulas)}
                    className="flex items-center gap-2 text-gray-400 hover:text-white text-sm"
                  >
                    <ChevronDown className={`w-4 h-4 transition-transform ${showAllFormulas ? 'rotate-180' : ''}`} />
                    Compare All Formulas
                  </button>
                  
                  {showAllFormulas && (
                    <div className="mt-3 space-y-2">
                      {Object.entries(oneRmResult.allFormulas).map(([key, { name, estimated1RM }]) => (
                        <div 
                          key={key}
                          className={`flex justify-between p-2 rounded-lg ${
                            key === formula ? 'bg-primary/20' : 'bg-secondary/30'
                          }`}
                        >
                          <span className="text-gray-300">{name}</span>
                          <span className="text-white font-medium">{estimated1RM} {unit}</span>
                        </div>
                      ))}
                      <div className="flex justify-between p-2 rounded-lg bg-blue-500/20 border border-blue-500/30">
                        <span className="text-blue-400">Average</span>
                        <span className="text-white font-medium">{oneRmResult.average} {unit}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="h-full flex items-center justify-center text-gray-500">
                <div className="text-center">
                  <Dumbbell className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p>Enter your lift details to calculate your 1RM</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Plate Calculator Tab */}
      {activeTab === 'plates' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Input Card */}
          <div className="bg-card border border-border rounded-xl p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Scale className="w-5 h-5 text-primary" />
              Plate Calculator
            </h2>
            
            <div className="space-y-4">
              <div className="p-4 bg-secondary/30 rounded-lg">
                <div className="text-sm text-gray-400 mb-1">Using Standard Olympic Bar</div>
                <div className="text-white font-semibold">
                  {unit === 'lbs' ? '45 lbs' : '20 kg'} bar
                </div>
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-1">Target Weight ({unit})</label>
                <input
                  type="number"
                  value={targetWeight}
                  onChange={(e) => setTargetWeight(e.target.value)}
                  className="w-full px-4 py-3 bg-secondary border border-border rounded-lg text-white text-lg"
                  placeholder={`e.g., ${unit === 'lbs' ? '260' : '120'}`}
                  data-testid="plate-weight-input"
                />
              </div>
              
              <Button 
                onClick={handleCalculatePlates}
                className="w-full bg-primary hover:bg-red-700 text-lg py-6"
                data-testid="calculate-plates-btn"
              >
                <Scale className="w-5 h-5 mr-2" />
                Calculate Plates
              </Button>
              
              {/* Available Plates Info */}
              <div className="p-4 bg-secondary/30 rounded-lg">
                <div className="text-sm text-gray-400 mb-2">Available Plates</div>
                <div className="flex flex-wrap gap-2">
                  {(unit === 'lbs' ? PLATES_LBS : PLATES_KG).map(plate => (
                    <span key={plate} className="px-2 py-1 bg-secondary rounded text-sm text-white">
                      {plate} {unit}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Results Card */}
          <div className="bg-card border border-border rounded-xl p-6">
            {plateResult ? (
              plateResult.valid ? (
                <div className="space-y-6">
                  <div className="text-center p-6 bg-gradient-to-br from-blue-500/20 to-blue-900/20 rounded-xl border border-blue-500/30">
                    <div className="text-gray-400 text-sm mb-1">Total Weight</div>
                    <div className="text-4xl font-bold text-white mb-2">
                      {plateResult.actualTotal} <span className="text-xl text-gray-400">{unit}</span>
                    </div>
                    {plateResult.remainder > 0 && (
                      <div className="text-yellow-400 text-sm">
                        (Target: {targetWeight} {unit} - {plateResult.remainder} {unit} short)
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <h3 className="text-white font-semibold mb-4">Plates Per Side</h3>
                    
                    {/* Visual representation */}
                    <div className="flex items-center justify-center gap-2 mb-6 p-4 bg-secondary/30 rounded-lg">
                      {/* Left plates */}
                      <div className="flex items-center">
                        {plateResult.breakdown.map(({ weight, count }, idx) => (
                          Array(count).fill(null).map((_, i) => (
                            <div
                              key={`left-${weight}-${i}`}
                              className={`h-16 rounded-sm mx-0.5 flex items-center justify-center text-xs font-bold ${
                                weight >= 45 ? 'w-6 bg-red-500' :
                                weight >= 25 ? 'w-5 bg-blue-500' :
                                weight >= 10 ? 'w-4 bg-yellow-500' :
                                'w-3 bg-green-500'
                              }`}
                              title={`${weight} ${unit}`}
                            >
                              <span className="rotate-90 text-white">{weight}</span>
                            </div>
                          ))
                        ))}
                      </div>
                      
                      {/* Bar */}
                      <div className="w-24 h-4 bg-gray-600 rounded-full" />
                      
                      {/* Right plates (mirrored) */}
                      <div className="flex items-center">
                        {[...plateResult.breakdown].reverse().map(({ weight, count }, idx) => (
                          Array(count).fill(null).map((_, i) => (
                            <div
                              key={`right-${weight}-${i}`}
                              className={`h-16 rounded-sm mx-0.5 flex items-center justify-center text-xs font-bold ${
                                weight >= 45 ? 'w-6 bg-red-500' :
                                weight >= 25 ? 'w-5 bg-blue-500' :
                                weight >= 10 ? 'w-4 bg-yellow-500' :
                                'w-3 bg-green-500'
                              }`}
                              title={`${weight} ${unit}`}
                            >
                              <span className="rotate-90 text-white">{weight}</span>
                            </div>
                          ))
                        ))}
                      </div>
                    </div>
                    
                    {/* Plate list */}
                    <div className="space-y-2">
                      {plateResult.breakdown.map(({ weight, count }) => (
                        <div 
                          key={weight}
                          className="flex justify-between items-center p-3 bg-secondary/50 rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold ${
                              weight >= 45 ? 'bg-red-500' :
                              weight >= 25 ? 'bg-blue-500' :
                              weight >= 10 ? 'bg-yellow-500' :
                              'bg-green-500'
                            }`}>
                              {weight}
                            </div>
                            <span className="text-white">{weight} {unit} plates</span>
                          </div>
                          <span className="text-2xl font-bold text-white">{count} × </span>
                        </div>
                      ))}
                    </div>
                    
                    <div className="mt-4 p-3 bg-secondary/30 rounded-lg text-sm text-gray-400">
                      <div className="flex justify-between">
                        <span>Bar:</span>
                        <span>{plateResult.barWeight} {unit}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Plates (both sides):</span>
                        <span>{plateResult.totalPlateWeight} {unit}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center text-red-400 p-6">
                  <Info className="w-12 h-12 mx-auto mb-4" />
                  <p>{plateResult.error}</p>
                </div>
              )
            ) : (
              <div className="h-full flex items-center justify-center text-gray-500">
                <div className="text-center">
                  <Scale className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p>Enter a target weight to see plate breakdown</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Timer Tab */}
      {activeTab === 'timer' && (
        <div className="max-w-xl mx-auto">
          <div className="bg-card border border-border rounded-xl p-6">
            {/* Timer Mode Toggle */}
            <div className="flex gap-2 mb-6 bg-secondary/50 rounded-lg p-1">
              <button
                onClick={() => { setTimerMode('stopwatch'); resetTimer(); }}
                className={`flex-1 py-2 rounded-md text-sm font-medium transition-colors ${
                  timerMode === 'stopwatch' ? 'bg-primary text-white' : 'text-gray-400 hover:text-white'
                }`}
              >
                Stopwatch
              </button>
              <button
                onClick={() => { setTimerMode('countdown'); resetTimer(); }}
                className={`flex-1 py-2 rounded-md text-sm font-medium transition-colors ${
                  timerMode === 'countdown' ? 'bg-primary text-white' : 'text-gray-400 hover:text-white'
                }`}
              >
                Countdown
              </button>
              <button
                onClick={() => { setTimerMode('interval'); setIntervalPhase('ready'); setTime(0); }}
                className={`flex-1 py-2 rounded-md text-sm font-medium transition-colors ${
                  timerMode === 'interval' ? 'bg-purple-500 text-white' : 'text-gray-400 hover:text-white'
                }`}
                data-testid="timer-mode-interval"
              >
                Intervals
              </button>
            </div>
            
            {/* Interval Training Settings */}
            {timerMode === 'interval' && intervalPhase === 'ready' && (
              <div className="space-y-4 mb-6 animate-in fade-in duration-200">
                {/* Presets */}
                <div>
                  <label className="block text-xs text-gray-400 mb-2">Quick Presets</label>
                  <div className="grid grid-cols-3 gap-2">
                    {INTERVAL_PRESETS.map(preset => (
                      <button
                        key={preset.name}
                        onClick={() => applyIntervalPreset(preset)}
                        className={`p-2 rounded-lg border text-center transition-colors ${
                          workSeconds === preset.work && restSeconds === preset.rest && totalRounds === preset.rounds
                            ? 'border-purple-500 bg-purple-500/20 text-white'
                            : 'border-border hover:border-gray-600 text-gray-400'
                        }`}
                        data-testid={`preset-${preset.name.toLowerCase()}`}
                      >
                        <div className="font-medium text-sm">{preset.name}</div>
                        <div className="text-xs opacity-70">{preset.work}s/{preset.rest}s×{preset.rounds}</div>
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Custom Settings */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <label className="block text-xs text-gray-400 mb-1">Work (sec)</label>
                    <input
                      type="number"
                      value={workSeconds}
                      onChange={(e) => setWorkSeconds(Math.max(5, parseInt(e.target.value) || 5))}
                      className="w-full px-3 py-2 bg-red-500/20 border border-red-500/50 rounded-lg text-white text-center text-lg font-bold"
                      min="5"
                      data-testid="interval-work-input"
                    />
                  </div>
                  <div className="text-center">
                    <label className="block text-xs text-gray-400 mb-1">Rest (sec)</label>
                    <input
                      type="number"
                      value={restSeconds}
                      onChange={(e) => setRestSeconds(Math.max(0, parseInt(e.target.value) || 0))}
                      className="w-full px-3 py-2 bg-blue-500/20 border border-blue-500/50 rounded-lg text-white text-center text-lg font-bold"
                      min="0"
                      data-testid="interval-rest-input"
                    />
                  </div>
                  <div className="text-center">
                    <label className="block text-xs text-gray-400 mb-1">Rounds</label>
                    <input
                      type="number"
                      value={totalRounds}
                      onChange={(e) => setTotalRounds(Math.max(1, parseInt(e.target.value) || 1))}
                      className="w-full px-3 py-2 bg-purple-500/20 border border-purple-500/50 rounded-lg text-white text-center text-lg font-bold"
                      min="1"
                      data-testid="interval-rounds-input"
                    />
                  </div>
                </div>
                
                {/* Warmup & Cooldown */}
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer flex-1">
                    <input
                      type="checkbox"
                      checked={includeWarmup}
                      onChange={(e) => setIncludeWarmup(e.target.checked)}
                      className="w-4 h-4 rounded bg-secondary border-border text-yellow-500 focus:ring-yellow-500"
                    />
                    <span className="text-sm text-gray-300">Warmup ({warmupSeconds}s)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer flex-1">
                    <input
                      type="checkbox"
                      checked={includeCooldown}
                      onChange={(e) => setIncludeCooldown(e.target.checked)}
                      className="w-4 h-4 rounded bg-secondary border-border text-blue-500 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-300">Cooldown ({cooldownSeconds}s)</span>
                  </label>
                </div>
                
                {/* Total Time */}
                <div className="text-center p-3 bg-secondary/50 rounded-lg">
                  <span className="text-gray-400 text-sm">Total workout: </span>
                  <span className="text-white font-bold">
                    {Math.floor(calculateTotalIntervalTime() / 60)}:{(calculateTotalIntervalTime() % 60).toString().padStart(2, '0')}
                  </span>
                </div>
              </div>
            )}

            {/* Countdown Settings */}
            {timerMode === 'countdown' && !isRunning && time === 0 && (
              <div className="flex items-center justify-center gap-4 mb-6">
                <div className="text-center">
                  <label className="block text-xs text-gray-400 mb-1">Minutes</label>
                  <input
                    type="number"
                    value={countdownMinutes}
                    onChange={(e) => setCountdownMinutes(Math.max(0, parseInt(e.target.value) || 0))}
                    className="w-20 px-3 py-2 bg-secondary border border-border rounded-lg text-white text-center text-xl"
                    min="0"
                    max="99"
                  />
                </div>
                <span className="text-2xl text-gray-400 mt-4">:</span>
                <div className="text-center">
                  <label className="block text-xs text-gray-400 mb-1">Seconds</label>
                  <input
                    type="number"
                    value={countdownSeconds}
                    onChange={(e) => setCountdownSeconds(Math.max(0, Math.min(59, parseInt(e.target.value) || 0)))}
                    className="w-20 px-3 py-2 bg-secondary border border-border rounded-lg text-white text-center text-xl"
                    min="0"
                    max="59"
                  />
                </div>
              </div>
            )}

            {/* Timer Display - Different for Interval Mode */}
            {timerMode === 'interval' ? (
              <div className="text-center py-8">
                {/* Phase Indicator */}
                <div className={`inline-block px-6 py-2 rounded-full text-lg font-bold mb-4 ${
                  intervalPhase === 'work' ? 'bg-red-500 text-white animate-pulse' :
                  intervalPhase === 'rest' ? 'bg-blue-500 text-white' :
                  intervalPhase === 'warmup' ? 'bg-yellow-500 text-black' :
                  intervalPhase === 'cooldown' ? 'bg-cyan-500 text-white' :
                  intervalPhase === 'complete' ? 'bg-green-500 text-white' :
                  'bg-gray-600 text-white'
                }`}>
                  {intervalPhase === 'work' && '💪 WORK!'}
                  {intervalPhase === 'rest' && '😮‍💨 REST'}
                  {intervalPhase === 'warmup' && '🔥 WARMUP'}
                  {intervalPhase === 'cooldown' && '❄️ COOLDOWN'}
                  {intervalPhase === 'complete' && '🎉 COMPLETE!'}
                  {intervalPhase === 'ready' && '⏱️ READY'}
                </div>
                
                {/* Phase Timer */}
                <div className={`text-7xl font-mono font-bold mb-2 ${
                  intervalPhase === 'work' ? 'text-red-400' :
                  intervalPhase === 'rest' ? 'text-blue-400' :
                  intervalPhase === 'warmup' ? 'text-yellow-400' :
                  intervalPhase === 'cooldown' ? 'text-cyan-400' :
                  'text-white'
                }`}>
                  {intervalPhase === 'ready' 
                    ? '00:00' 
                    : `${Math.floor(phaseTimeRemaining / 60000).toString().padStart(2, '0')}:${Math.floor((phaseTimeRemaining % 60000) / 1000).toString().padStart(2, '0')}`
                  }
                </div>
                
                {/* Round Counter */}
                {(intervalPhase === 'work' || intervalPhase === 'rest') && (
                  <div className="text-gray-400 text-lg mb-2">
                    Round <span className="text-white font-bold">{currentRound}</span> of <span className="text-white">{totalRounds}</span>
                  </div>
                )}
                
                {/* Progress Bar */}
                {intervalPhase !== 'ready' && intervalPhase !== 'complete' && (
                  <div className="max-w-xs mx-auto">
                    <div className="h-2 bg-secondary rounded-full overflow-hidden">
                      <div 
                        className={`h-full transition-all duration-100 ${
                          intervalPhase === 'work' ? 'bg-red-500' :
                          intervalPhase === 'rest' ? 'bg-blue-500' :
                          intervalPhase === 'warmup' ? 'bg-yellow-500' :
                          'bg-cyan-500'
                        }`}
                        style={{ 
                          width: `${100 - (phaseTimeRemaining / ((
                            intervalPhase === 'work' ? workSeconds :
                            intervalPhase === 'rest' ? restSeconds :
                            intervalPhase === 'warmup' ? warmupSeconds :
                            cooldownSeconds
                          ) * 1000) * 100)}%` 
                        }}
                      />
                    </div>
                  </div>
                )}
                
                {/* Total Elapsed */}
                <div className="text-gray-500 text-sm mt-4">
                  Total: {formatTime(time)}
                </div>
              </div>
            ) : (
              /* Regular Timer Display */
              <div className="text-center py-8">
                <div className="text-7xl font-mono font-bold text-white mb-2">
                  {formatTime(time)}
                </div>
                <div className="text-gray-400 text-sm">
                  {timerMode === 'stopwatch' ? 'Stopwatch' : 'Countdown Timer'}
                </div>
              </div>
            )}

            {/* Timer Controls */}
            <div className="flex items-center justify-center gap-4 mb-6">
              {!isRunning ? (
                <Button
                  onClick={startTimer}
                  className="w-20 h-20 rounded-full bg-green-600 hover:bg-green-700"
                  data-testid="timer-start-btn"
                >
                  <Play className="w-8 h-8" />
                </Button>
              ) : (
                <Button
                  onClick={pauseTimer}
                  className="w-20 h-20 rounded-full bg-yellow-600 hover:bg-yellow-700"
                  data-testid="timer-pause-btn"
                >
                  <Pause className="w-8 h-8" />
                </Button>
              )}
              
              <Button
                onClick={resetTimer}
                variant="outline"
                className="w-16 h-16 rounded-full border-border"
                data-testid="timer-reset-btn"
              >
                <RotateCcw className="w-6 h-6" />
              </Button>
            </div>

            {/* Alert Settings */}
            <div className="border-t border-border pt-6">
              <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                <Bell className="w-4 h-4" />
                Alert Type
              </h3>
              <div className="flex gap-2">
                <button
                  onClick={() => setAlertType('music')}
                  className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-lg border transition-colors ${
                    alertType === 'music' 
                      ? 'border-primary bg-primary/20 text-primary' 
                      : 'border-border text-gray-400 hover:text-white'
                  }`}
                  data-testid="alert-music-btn"
                >
                  <Music className="w-5 h-5" />
                  Music
                  {alertType === 'music' && <Check className="w-4 h-4" />}
                </button>
                <button
                  onClick={() => setAlertType('sound')}
                  className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-lg border transition-colors ${
                    alertType === 'sound' 
                      ? 'border-primary bg-primary/20 text-primary' 
                      : 'border-border text-gray-400 hover:text-white'
                  }`}
                  data-testid="alert-sound-btn"
                >
                  <Volume2 className="w-5 h-5" />
                  Sound
                  {alertType === 'sound' && <Check className="w-4 h-4" />}
                </button>
                <button
                  onClick={() => setAlertType('vibrate')}
                  className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-lg border transition-colors ${
                    alertType === 'vibrate' 
                      ? 'border-primary bg-primary/20 text-primary' 
                      : 'border-border text-gray-400 hover:text-white'
                  }`}
                  data-testid="alert-vibrate-btn"
                >
                  <Vibrate className="w-5 h-5" />
                  Vibrate
                  {alertType === 'vibrate' && <Check className="w-4 h-4" />}
                </button>
              </div>
              
              {/* High-Energy Song Selection - Only show when Music is selected */}
              {alertType === 'music' && (
                <div className="mt-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-gray-400">Select Pump-Up Track</div>
                    <button
                      onClick={testAlert}
                      className="flex items-center gap-1 text-xs text-primary hover:text-red-400 transition-colors"
                      data-testid="test-alert-btn"
                    >
                      <PlayCircle className="w-4 h-4" />
                      Preview
                    </button>
                  </div>
                  <div className="grid gap-2">
                    {Object.entries(PUMP_UP_SONGS).map(([key, song]) => (
                      <button
                        key={key}
                        onClick={() => setSelectedSong(key)}
                        className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${
                          selectedSong === key 
                            ? 'border-primary bg-gradient-to-r from-primary/20 to-red-900/20 text-white' 
                            : 'border-border text-gray-400 hover:text-white hover:border-gray-600'
                        }`}
                        data-testid={`song-${key}`}
                      >
                        <span className="text-2xl">{song.icon}</span>
                        <div className="flex-1 text-left">
                          <div className="font-medium">{song.name}</div>
                          <div className="text-xs text-gray-500">{song.description}</div>
                        </div>
                        {selectedSong === key && <Check className="w-5 h-5 text-primary" />}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Quick Presets */}
              {timerMode === 'countdown' && (
                <div className="mt-4">
                  <div className="text-xs text-gray-400 mb-2">Quick Presets</div>
                  <div className="flex gap-2 flex-wrap">
                    {[
                      { label: '30s', mins: 0, secs: 30 },
                      { label: '1 min', mins: 1, secs: 0 },
                      { label: '1:30', mins: 1, secs: 30 },
                      { label: '2 min', mins: 2, secs: 0 },
                      { label: '3 min', mins: 3, secs: 0 },
                      { label: '5 min', mins: 5, secs: 0 }
                    ].map(({ label, mins, secs }) => (
                      <button
                        key={label}
                        onClick={() => {
                          setCountdownMinutes(mins);
                          setCountdownSeconds(secs);
                          setTime((mins * 60 + secs) * 1000);
                          setIsRunning(false);
                        }}
                        className="px-3 py-1.5 bg-secondary hover:bg-secondary/80 rounded-lg text-sm text-gray-300"
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            {/* Workout Mode Section */}
            <div className="border-t border-border pt-6 mt-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-medium flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-purple-400" />
                  Workout Mode
                </h3>
                <button
                  onClick={() => setWorkoutModeEnabled(!workoutModeEnabled)}
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    workoutModeEnabled ? 'bg-purple-500' : 'bg-gray-600'
                  }`}
                  data-testid="workout-mode-toggle"
                >
                  <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                    workoutModeEnabled ? 'left-7' : 'left-1'
                  }`} />
                </button>
              </div>
              
              {workoutModeEnabled && (
                <div className="space-y-4 animate-in fade-in duration-200">
                  <p className="text-sm text-gray-400">
                    Auto-play Spotify workout music when timer starts
                  </p>
                  
                  {/* Spotify Connection Status */}
                  {!spotifyConnected ? (
                    <div className="p-4 bg-gradient-to-r from-green-900/30 to-green-600/20 border border-green-500/30 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Link2Off className="w-5 h-5 text-gray-400" />
                          <span className="text-gray-300">Spotify not connected</span>
                        </div>
                        <Button
                          onClick={connectToSpotify}
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                          data-testid="connect-spotify-timer-btn"
                        >
                          <Music className="w-4 h-4 mr-1" />
                          Connect
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg flex items-center gap-2">
                        <Link2 className="w-4 h-4 text-green-400" />
                        <span className="text-green-400 text-sm">Spotify Connected</span>
                      </div>
                      
                      {/* Workout Type Selection */}
                      <div>
                        <label className="block text-xs text-gray-400 mb-2">Workout Type</label>
                        <div className="grid grid-cols-3 gap-2">
                          {WORKOUT_TYPES.map(type => {
                            const Icon = type.icon;
                            return (
                              <button
                                key={type.id}
                                onClick={() => setWorkoutType(type.id)}
                                className={`flex flex-col items-center gap-1 p-2 rounded-lg border transition-all ${
                                  workoutType === type.id
                                    ? 'border-purple-500 bg-purple-500/20'
                                    : 'border-border hover:border-gray-600'
                                }`}
                                data-testid={`workout-type-${type.id}`}
                              >
                                <Icon className={`w-4 h-4 ${workoutType === type.id ? type.color : 'text-gray-400'}`} />
                                <span className={`text-xs ${workoutType === type.id ? 'text-white' : 'text-gray-400'}`}>
                                  {type.name}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      
                      {/* Now Playing (when music is active) */}
                      {currentTrack && (
                        <div className="p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                          <div className="flex items-center gap-3">
                            {currentTrack.album_image && (
                              <img src={currentTrack.album_image} alt="" className="w-12 h-12 rounded" />
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="text-white font-medium truncate text-sm">{currentTrack.name}</div>
                              <div className="text-gray-400 text-xs truncate">{currentTrack.artists?.join(', ')}</div>
                            </div>
                            <div className="flex items-center gap-1">
                              <button
                                onClick={skipToPreviousTrack}
                                className="p-1.5 text-gray-400 hover:text-white"
                              >
                                <SkipBack className="w-4 h-4" />
                              </button>
                              <button
                                onClick={toggleMusicPlayback}
                                className="p-2 bg-purple-500 rounded-full hover:bg-purple-600"
                                data-testid="toggle-music-btn"
                              >
                                {isPlayingMusic ? (
                                  <Pause className="w-4 h-4 text-white" />
                                ) : (
                                  <Play className="w-4 h-4 text-white" />
                                )}
                              </button>
                              <button
                                onClick={skipToNextTrack}
                                className="p-1.5 text-gray-400 hover:text-white"
                              >
                                <SkipForward className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                          {isPlayingMusic && (
                            <div className="flex gap-0.5 justify-center mt-2">
                              <span className="w-1 h-3 bg-purple-500 animate-pulse rounded"></span>
                              <span className="w-1 h-4 bg-purple-500 animate-pulse delay-75 rounded"></span>
                              <span className="w-1 h-2 bg-purple-500 animate-pulse delay-150 rounded"></span>
                              <span className="w-1 h-5 bg-purple-500 animate-pulse delay-100 rounded"></span>
                              <span className="w-1 h-3 bg-purple-500 animate-pulse delay-200 rounded"></span>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Loading state */}
                      {isGeneratingPlaylist && (
                        <div className="flex items-center justify-center gap-2 p-4 text-purple-400">
                          <Loader2 className="w-5 h-5 animate-spin" />
                          <span>Generating workout playlist...</span>
                        </div>
                      )}
                      
                      {/* Playlist info */}
                      {workoutPlaylist && !isGeneratingPlaylist && (
                        <div className="text-xs text-gray-400 text-center">
                          {workoutPlaylist.track_count} tracks • {workoutPlaylist.actual_duration_minutes} min
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
